import { faQuestionCircle as solidQuestionCircle } from "@jewlr/pro-solid-svg-icons/faQuestionCircle"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import { connect } from "react-redux"
import ReactTooltip from "react-tooltip"
import styled from "styled-components"

import {
  updateChoice,
  fetchStylecode,
  trackSlide,
} from "areas/product/store/actions"
import {
  ChoiceOption,
  GradientContainer,
  OptionTitle,
  OptionPrice,
  OptionsSlider,
} from "areas/product/wizard-options/components"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Image from "components/ui/image"
import Media from "components/ui/media"
import { gemstones } from "helpers/gemstones"
import { to_currency } from "helpers/number"
import { imageLookup } from "helpers/product/image-lookup"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

const OptionDetailsAttribute = ({
  className = "",
  isTitle = false,
  ...props
}) => (
  <p
    className={cn(
      "text-12 tracking-half",
      isTitle ? "text-black" : "text-grey-500",
      className
    )}
    {...props}
  />
)

OptionDetailsAttribute.propTypes = {
  className: PropTypes.string,
  isTitle: PropTypes.bool,
}

const Tooltip = styled(ReactTooltip)`
  color: ${(props) => props.theme.colors.white};
  margin-left: 10px;
  max-width: 300px;
  text-transform: none;
`
const Stones = ({ category, data, selectedValue, setChoice }) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  const hasStoneSelected = category.options.some(
    (opt) => opt.jewlrValue === selectedValue.jewlrValue
  )

  return (
    <OptionsSlider
      aria-label={`${data.heading.title} Options`}
      data-slider="true"
      role="radiogroup"
    >
      {category.options.map((stone, index) => {
        const selected = selectedValue.jewlrValue == stone.jewlrValue
        return (
          <ChoiceOption
            aria-checked={selected}
            data-cy={`product-wizard-option-${data.pref}-${stone.value}`}
            data-is-option="true"
            data-is-selected={selected}
            key={`stoneCategory-${stone.imageKey}`}
            onClick={() => setChoice(data.pref, stone)}
            role="radio"
            tabIndex={selected || (index === 0 && !hasStoneSelected) ? 0 : -1}
            {...keyboardProps}
          >
            {stone.use_large_icons ? (
              <Image
                alt={stone.title}
                height="60"
                sizes="60px"
                src={imageLookup("stone", stone.imageKey).x2}
                srcSet={
                  imageLookup("stone", stone.imageKey).x1 +
                  " 1x," +
                  imageLookup("stone", stone.imageKey).x2 +
                  " 2x"
                }
                title={stone.title}
                width="60"
              />
            ) : (
              <Image
                alt={stone.title}
                height="40"
                sizes="40px"
                src={imageLookup("stone", stone.imageKey).x2}
                srcSet={
                  imageLookup("stone", stone.imageKey).x1 +
                  " 1x," +
                  imageLookup("stone", stone.imageKey).x2 +
                  " 2x"
                }
                title={stone.title}
                width="40"
              />
            )}
            <OptionTitle className="tablet:hidden">
              {stone.info}
              <br />
              {!data.hide_attributes && stone.subnote && (
                <em>{stone.subnote}</em>
              )}
            </OptionTitle>
            {stone.regular_price ? (
              <OptionPrice>
                <span>
                  <strike>{to_currency(stone.regular_price)}</strike>{" "}
                </span>
                <OptionPrice className="text-sale">
                  {to_currency(stone.price)}
                </OptionPrice>
              </OptionPrice>
            ) : (
              <OptionPrice>
                {stone.price == 0 && data.hide_zero_surcharge
                  ? ""
                  : to_currency(stone.price)}
              </OptionPrice>
            )}
          </ChoiceOption>
        )
      })}
    </OptionsSlider>
  )
}
Stones.propTypes = {
  category: PropTypes.object,
  data: PropTypes.object,
  selectedValue: PropTypes.object,
  setChoice: PropTypes.func,
}

const InternalStoneOptions = ({ data, selectedValue, setChoice }) => {
  if (
    typeof data.heading === "undefined" ||
    data.disabled ||
    (data.options.length <= 1 && !data.override_text)
  ) {
    return null
  }

  const [stoneDetails, setStoneDetails] = useState(selectedValue)
  const [selectedCategory, setSelectedCategory] = useState(null)

  // Keep local state (stoneDetails) sync with the user choice in Redux data (selectedValue)
  useEffect(() => {
    if (
      stoneDetails.value !== selectedValue.value ||
      stoneDetails.price !== selectedValue.price
    ) {
      setStoneDetails(selectedValue)
    }
    const category = data.categories.find((category) =>
      category.options.some(
        (option) => option.title?.trim() === selectedValue.title?.trim()
      )
    )
    if (category !== selectedCategory) {
      setSelectedCategory(category)
    }
  }, [selectedValue])

  const stoneExtraDetails = gemstones.find((gs) =>
    gs.code.includes(stoneDetails.jewlrValue)
  )

  return (
    <div data-horizontal-scroll="true">
      <Media
        className={cn(
          "border-b-grey-20 mb-10 border-b border-dotted pb-10",
          data.hide_attributes
            ? "min-(--min-height)"
            : "min-(--tablet-min-height)"
        )}
        greaterThanOrEqual="tablet"
        style={{
          "--min-height": "50px",
          "--tablet-min-height": "77px",
        }}
      >
        <OptionDetailsAttribute isTitle={true}>
          {stoneDetails.stoneName}- {to_currency(stoneDetails.price)}
        </OptionDetailsAttribute>
        {data.stone_size && (
          <OptionDetailsAttribute
            data-cy={`product-wizard-option-stone-size-${data.pref}`}
          >
            Stone Size:{" "}
            <span dangerouslySetInnerHTML={{ __html: data.stone_size }} />
          </OptionDetailsAttribute>
        )}
        {!data.hide_attributes && (
          <div>
            <OptionDetailsAttribute>
              {stoneDetails.birthmonth &&
                "Birthmonth: " + stoneDetails.birthmonth}
            </OptionDetailsAttribute>
            <OptionDetailsAttribute>
              {stoneExtraDetails?.traits && (
                <>
                  <span>Attributes: </span>
                  <span>{stoneExtraDetails.traits.join(", ")}</span>
                </>
              )}
            </OptionDetailsAttribute>
          </div>
        )}
      </Media>

      {data.categories.map((category, index) => (
        <div
          className="border-b-grey-10 tablet:border-b-0 mb-10 border-b last:border-b-0"
          key={`category-${category.title}`}
        >
          {!data.hide_categories && category.title && (
            <p className="text-14 px-15 tablet:px-10 mt-10 font-bold tracking-wide text-black">
              {category.title}{" "}
              <FontAwesomeIcon
                className="text-13"
                data-for={`wizard-stone__tip_${data.pref}_${index}`}
                data-tip
                icon={solidQuestionCircle}
              />
            </p>
          )}
          <Tooltip
            id={`wizard-stone__tip_${data.pref}_${index}`}
            multiline
            place="right"
          >
            {category.help_text}
          </Tooltip>
          <GradientContainer>
            <FocusScope>
              <Stones
                category={category}
                data={data}
                selectedValue={selectedValue}
                setChoice={setChoice}
              />
            </FocusScope>
          </GradientContainer>
        </div>
      ))}
    </div>
  )
}

InternalStoneOptions.propTypes = {
  data: PropTypes.object,
  selectedValue: PropTypes.object,
  setChoice: PropTypes.func,
}

const getSelectedValue = (state, ownProps) => {
  if (typeof state.choices[ownProps.pref] === "undefined") {
    return {}
  } else {
    return state.choices[ownProps.pref]
  }
}

const getData = (state, ownProps) => {
  if (typeof state.wizard.data[ownProps.pref] !== "undefined") {
    return state.wizard.data[ownProps.pref]
  } else {
    return { pref: ownProps.pref }
  }
}

const mapStateToProps = (state, ownProps) => {
  const optionData = getData(state, ownProps)
  const selectedValue = getSelectedValue(state, ownProps)
  return {
    data: optionData, // TODO: Use a better name for the prop e.g. optionData
    selectedValue: selectedValue,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    setChoice: (pref, data) => {
      dispatch(updateChoice(pref, data, true))

      // Style code switcher
      if (data.style_code) {
        dispatch(fetchStylecode(data.style_code))
      } else if (data.track_slide) {
        dispatch(trackSlide(data.track_slide))
      }

      try {
        if (typeof ownProps.callback !== "undefined" && ownProps.callback) {
          ownProps.callback()
        }
      } catch (error) {
        console.log("CALLBACK ERROR:", error)
      }
    },
  }
}

const StoneOptions = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalStoneOptions)

export default StoneOptions
